import React, { useMemo, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { Autocomplete, Chip, CircularProgress, ListItemText, MenuItem, TextField } from '@mui/material';

import { SEARCH_DEBOUNCE_MS } from '~/consts/tours';

import useDebounced from '~/hooks/useDebounced';

import SearchService, { TypeaheadBrand, TypeaheadType } from '~/services/SearchService';

import { RELEVANT_TYPEAHEAD_TYPES } from '../TourDetails/RJSF/LELocationSearchWidget';

interface Props {
  label: string;
  /** As the tour form stores them: "[lePlaceId] name, area", or a name typed before the search existed. */
  value: Array<string>;
  onChange: (value: Array<string>) => void;
  multiple?: boolean;
  types?: Array<TypeaheadType>;
  helperText?: string;
}

const MIN_QUERY_LENGTH = 2;
const RESULTS_LIMIT = 20;

/** The name a stored location shows, without its place id. */
export function locationLabel(value: string): string {
  return value.replace(/^\[[^\]]+\]\s*/, '');
}

/** Locations from the svc-search typeahead, kept in the form the tour form's save reads. */
export default function NativeTourLocationSearch({
  label,
  value,
  onChange,
  multiple = true,
  types = RELEVANT_TYPEAHEAD_TYPES,
  helperText,
}: Props) {
  const tenant = useSelector<App.State, App.Tenant>((state) => state.tenant);
  const [inputValue, setInputValue] = useState('');
  const query = useDebounced(inputValue.trim(), SEARCH_DEBOUNCE_MS);

  const { data: results = [], isFetching } = useQuery({
    queryKey: ['native-tour-location-search', query, types, tenant?.brand],
    queryFn: async () =>
      (
        await SearchService.typeahead(query, {
          type: types,
          brand: (tenant?.brand as TypeaheadBrand) ?? 'luxuryescapes',
          region: 'AU',
          limit: RESULTS_LIMIT,
          priority: 'current',
        })
      ).result,
    enabled: query.length >= MIN_QUERY_LENGTH,
  });

  const options = useMemo(
    () =>
      results.map((result) => ({
        value: `[${result.id}] ${result.format.mainText}${result.format.secondaryText ? `, ${result.format.secondaryText}` : ''}`,
        title: result.format.mainText,
        subtitle: result.format.secondaryText,
      })),
    [results],
  );
  const subtitles = new Map(options.map((option) => [option.value, option.subtitle]));

  return (
    <Autocomplete<string, boolean>
      multiple={multiple}
      value={multiple ? value : (value[0] ?? null)}
      onChange={(_event, next) => {
        const picked = Array.isArray(next) ? next : next ? [next] : [];
        onChange(picked);
      }}
      onInputChange={(_event, next, reason) => reason === 'input' && setInputValue(next)}
      options={options.map((option) => option.value)}
      filterOptions={(all) => all}
      getOptionLabel={locationLabel}
      loading={isFetching}
      noOptionsText={query.length < MIN_QUERY_LENGTH ? 'Type to search' : 'No places found'}
      renderOption={(props, option) => (
        <MenuItem {...props} key={option} dense>
          <ListItemText primary={locationLabel(option).split(',')[0]} secondary={subtitles.get(option)} />
        </MenuItem>
      )}
      renderTags={(values, getTagProps) =>
        values.map((option, index) => (
          <Chip {...getTagProps({ index })} key={option} size="small" label={locationLabel(option)} />
        ))
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isFetching ? <CircularProgress size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
